import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { categoryData } from '../data/categoryData';
import { ArrowRight, Maximize2, Shapes, Layers, Zap, ChevronRight } from 'lucide-react';
import { CustomQuoteModal } from '../components/CustomQuoteModal';
import { QuoteSubmittedToast } from '../components/QuoteSubmittedToast';
import { Seo } from '../components/Seo';
import { SITE, localBusinessJsonLd } from '../data/seoData';

// Hero asset — autoplay product video bundled with the build. The
// previous static Stickers.png + Made-in-Canada stamp pair was
// retired in iter114s.41 in favour of the supplied 720p MP4. The
// PixVerse.ai watermark (top-right at y=30-90 in the original
// 1200×800 frame) was removed cleanly in iter114s.43 by ffmpeg-
// cropping the top 120 px (`crop=1200:680:0:120`) directly from
// the original source — no inpainting blur, no smudge artifacts,
// just a tighter zoom on the actual product. The poster is the
// first frame extracted from the cropped stream so the still
// preview matches the playback exactly.
const HERO_VIDEO_SRC = '/eg3-hero.mp4';
const HERO_POSTER_SRC = '/eg3-hero-poster.jpg';

const FEATURE_BAR = [
  { icon: Maximize2, label: 'Any Size' },
  { icon: Shapes, label: 'Any Shape' },
  { icon: Layers, label: 'Any Quantity' },
  { icon: Zap, label: 'Fast Turnaround' },
];

const SplashPage = () => {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [customQuoteOpen, setCustomQuoteOpen] = useState(false);
  const [quoteSentToastOpen, setQuoteSentToastOpen] = useState(false);

  const categories = Object.values(categoryData);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Custom Stickers, Labels, Decals & Patches | Evergreen Graphics Group"
        description="Order custom die-cut stickers, roll labels, vinyl decals, DTF transfers & patches online. Instant quotes, AI mockups & fast shipping across Canada & the USA."
        path="/"
        jsonLd={localBusinessJsonLd()}
      />
      {/* ================== HERO ================== */}
      <section className="bg-[#F4F7F2]" data-testid="splash-hero">
        {/* iter114s.59 — Hero padding tightened (py-8/12 vs former
            py-16/24) so the "Explore Our Most Popular Custom Products"
            section peeks above the fold on a 1080p initial paint
            without forcing a scroll. Grid gap, heading scale, and the
            feature-bar bottom padding also reduced proportionally so
            the layout stays balanced. */}
        <div className="max-w-7xl mx-auto px-6 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* LEFT — headline + description + CTAs. */}
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-[#14281D]">
              Bring your brand to life with{' '}
              <span className="font-extrabold text-[#6FBF73]">Custom</span>{' '}
              Stickers, Labels, Decals &amp; More
            </h1>
            <p className="mt-4 text-sm md:text-base text-[#1F3D2C]/80 leading-relaxed max-w-xl">
              Easily create &amp; print custom products for your business, brand, and beyond.
              Order in any size, die-cut shape, and quantity — printed in Canada and shipped to your door.
            </p>
            <div className="mt-6 flex flex-wrap items-start gap-4">
              {/* iter114s.62 — Each primary CTA now carries a short
                  supporting caption below it so the choice between
                  "premium / consultative" and "fast / self-serve" is
                  obvious at a glance. */}
              <div className="flex flex-col items-start max-w-[260px]">
                <button
                  type="button"
                  onClick={() => setCustomQuoteOpen(true)}
                  className="group inline-flex items-center gap-2 bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-6 py-3 rounded-full text-sm md:text-base font-semibold shadow-md hover:shadow-lg transition-all"
                  data-testid="hero-custom-quote-cta"
                >
                  Get a Custom Quote
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <p
                  className="mt-2 text-xs md:text-sm text-[#1F3D2C]/70 leading-snug"
                  data-testid="hero-custom-quote-caption"
                >
                  Premium materials, custom finishes, and tailored pricing.
                </p>
              </div>
              <div className="flex flex-col items-start max-w-[260px]">
                <Link
                  to="/pricing-catalog"
                  data-testid="hero-instant-quote-cta"
                  className="group inline-flex items-center gap-2 bg-white border-2 border-[#6FBF73] text-[#2F5D3A] hover:bg-[#6FBF73] hover:text-white px-6 py-3 rounded-full text-sm md:text-base font-semibold transition-all"
                >
                  Get an Instant Online Quote
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <p
                  className="mt-2 text-xs md:text-sm text-[#1F3D2C]/70 leading-snug"
                  data-testid="hero-instant-quote-caption"
                >
                  Quickly compare materials, finishes, and quantities live.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT — autoplay hero product video.
              iter114s.41 — Static Stickers.png + Made-in-Canada
              stamp retired in favour of the supplied 720p product
              loop. `playsInline` keeps it inline on iOS Safari
              (otherwise the OS hijacks playback into a fullscreen
              modal); `muted` is required for the browser to honour
              `autoPlay`. `preload="auto"` ensures the first frame
              paints immediately on page-load instead of staying
              blank until the user mouses over the element. */}
          <div className="relative" data-testid="hero-collage-wrapper">
            <video
              src={HERO_VIDEO_SRC}
              className="w-full h-auto rounded-3xl shadow-xl object-cover aspect-[5/4]"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={HERO_POSTER_SRC}
              data-testid="hero-video"
            />
          </div>
        </div>

        {/* Feature bar — floating white card spanning the section */}
        <div className="max-w-7xl mx-auto px-6 pb-8">
          <div
            className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm grid grid-cols-2 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#E5E7EB]"
            data-testid="feature-bar"
          >
            {FEATURE_BAR.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center justify-center gap-3 py-3 px-4 text-[#14281D]"
                data-testid={`feature-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5 text-[#6FBF73]" strokeWidth={2} />
                <span className="text-sm sm:text-base font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================== POPULAR PRODUCTS ================== */}
      <section className="max-w-7xl mx-auto px-6 py-10 md:py-14">
        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-[#14281D]">
            <span className="text-[#6FBF73]">Explore</span> Our Most Popular Custom Products
          </h2>
          {/* iter114s.62 — Supporting tagline that reinforces the
              "everything in one place" value-prop the operator wants
              to land in the customer's first scroll. */}
          <p
            className="mt-3 text-sm md:text-base text-[#1F3D2C]/75 max-w-3xl leading-relaxed"
            data-testid="popular-products-tagline"
          >
            Get a complete online buying experience with product walkthroughs, instant quotes, live mockups, and direct checkout.
          </p>
        </div>

        {/* Categories Grid — restyled cards matching StickerYou layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/category/${category.id}`}
              className="group relative block rounded-2xl overflow-hidden bg-white border border-[#E5E7EB] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              onMouseEnter={() => setHoveredCategory(category.id)}
              onMouseLeave={() => setHoveredCategory(null)}
              data-testid={`category-card-${category.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#F4F7F2]">
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                {/* Hover summary overlay — iter114s.69: container BG flipped
                    from white/95 to EG3 light-green #6FBF73 (matches the
                    recoloured summary PNGs underneath) so the box fills
                    edge-to-edge in brand green AND content stays uncropped
                    (object-contain keeps the spec sheet within the frame
                    while the matching-colour container bleeds to the edges
                    — "fills the section box" without losing the labels). */}
                <div
                  className={`absolute inset-0 bg-[#6FBF73] flex items-center justify-center transition-opacity duration-300 ${
                    hoveredCategory === category.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <img
                    src={category.summaryImage}
                    alt={`${category.name} options`}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="p-5 flex items-center justify-between gap-3">
                <h3 className="text-lg md:text-xl font-semibold text-[#14281D] tracking-tight">{category.name}</h3>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#6FBF73]/10 text-[#6FBF73] group-hover:bg-[#6FBF73] group-hover:text-white transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ================== NOT-SURE CTA ================== */}
      <section className="bg-[#F4F7F2] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#1F3D2C] text-lg mb-6">Not sure what you need?</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/quote" data-testid="splash-quote-cta">
              <button className="bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-6 py-3 rounded-full text-base font-semibold shadow-md transition-colors">
                Start with Quick Quote
              </button>
            </Link>
            <Link to="/pricing-catalog" data-testid="splash-catalog-cta">
              <button className="bg-white border border-[#6FBF73] text-[#4A7C59] hover:bg-[#6FBF73] hover:text-white px-6 py-3 rounded-full text-base font-semibold transition-colors">
                Browse Live Pricing Catalog
              </button>
            </Link>
            <button
              type="button"
              onClick={() => setCustomQuoteOpen(true)}
              className="bg-[#2F5D3A] hover:bg-[#14281D] text-white px-6 py-3 rounded-full text-base font-semibold transition-colors shadow-md"
              data-testid="splash-custom-quote-cta"
            >
              Request a Custom Quote
            </button>
          </div>
        </div>
      </section>

      {/* ================== QUICK-START BUNDLES — REMOVED iter114s.38 ==================
          Operator asked to delete the Quick-Start Bundles section
          entirely. The 6 hand-picked bundle cards (Startup Pack,
          Influencer Drop, Brewery Starter, Event Swag Kit, Sample
          Pack, Merch Drop) are no longer surfaced on the splash. The
          /bundles page (if any) and the underlying bundles.js data
          file remain intact in case the bundles get re-introduced
          later. */}

      {/* ================== FOOTER ================== */}
      <footer className="bg-[#14281D] text-white py-10 mt-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-100">© 2026 Evergreen Graphics Group Inc. All rights reserved.</p>
          <p className="text-sm text-emerald-200/80 mt-2">
            {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}
          </p>
          <p className="text-sm text-emerald-200/80 mt-1">Phone: {SITE.phone} | Email: {SITE.email}</p>
        </div>
      </footer>

      <CustomQuoteModal
        open={customQuoteOpen}
        onClose={() => setCustomQuoteOpen(false)}
        onSubmitted={() => {
          setCustomQuoteOpen(false);
          setQuoteSentToastOpen(true);
        }}
      />
      <QuoteSubmittedToast
        open={quoteSentToastOpen}
        onClose={() => setQuoteSentToastOpen(false)}
      />
    </div>
  );
};

export default SplashPage;
